import clsx from 'clsx';
import { useState } from 'react';
import { Link } from 'wouter';
import { OrdersIcon } from '../icons/orders';
import { IronIcon } from '../icons/iron';
import { HistoryIcon } from '../icons/history';
import { Parameter } from '../shared/parameter';
import { WarningIcon } from '../icons/warning';
import { format_percentage } from '../shared/formatters';
import { SidebarHeader } from '../shared/sidebar-header';
import { ExpectedTime } from '../shared/expected-time';
import { ExpandedSidebar } from '../shared/expanded-sidebar';
import { WasteValue } from '../shared/waste-value';
import { useMachineStatus } from '../hooks/use-machine-status';
import { ResLoading } from '../shared/res-loading';

export const Sidebar = () => {
  const [expanded, setExpanded] = useState(false);
  const { data: line, isLoading, error } = useMachineStatus();

  if (!line || isLoading) return <ResLoading />;
  if (error) return <div>Error: {error.message}</div>;

  const warnings = line.current.alarms.filter((a) => a.type === 'WARNING');
  const errors = line.current.alarms.filter((a) => a.type === 'ALARM');

  return (
    <div className="relative w-1/4 py-4 px-8 bg-black text-white flex flex-col justify-around">
      <SidebarHeader
        title="Parametri Linea"
        expanded={expanded}
        onExpand={() => setExpanded((prev) => !prev)}
      />
      <Parameter label="Ordine corrente" value={line.current.order} />
      <hr className="opacity-20" />
      <Parameter label="Progr. saldatura" value={line.current.program} />
      <hr className="opacity-20" />
      <Parameter label="Codice articolo" value={line.current.article} />
      <hr className="opacity-20" />
      <Parameter
        label="Descr. articolo"
        value={line.current.article_description}
      />
      <hr className="opacity-20" />
      <Parameter
        label="Tempo atteso completamento"
        value={<ExpectedTime end_by={line.current.expected_end_time} />}
        unit="(hh:mm:ss)"
        size="l"
      />
      <hr className="opacity-20" />
      <Parameter
        label="Buoni / Previsti"
        value={
          <span className="w-full flex gap-2 justify-end items-end">
            <span className="text-green-500 text-4xl font-bold">
              {line.current.goods}
            </span>
            <span className="text-neutral-500 font-thin text-4xl">/</span>
            <span className="text-white text-4xl font-bold">
              {line.current.expected_goods}
            </span>
          </span>
        }
        unit={
          <span>
            <b>
              {format_percentage(
                line.current.goods / line.current.expected_goods,
              )}
            </b>{' '}
            completamento
          </span>
        }
        size="l"
      />
      <hr className="opacity-20" />
      <Parameter
        label="Scarti"
        value={<WasteValue value={line.current.wastes} />}
        size="l"
      />
      <hr className="opacity-20" />
      <Parameter
        label="OEE Linea"
        value={
          <span className="text-green-500">{format_percentage(line.oee)}</span>
        }
        size="l"
      />
      <hr className="opacity-20" />
      <Parameter
        label="Consumo Linea"
        value={line.current.energy}
        unit="(kWh)"
        size="l"
      />
      <hr className="opacity-20" />
      <Parameter
        label="Potenza Istantanea"
        value={line.current.power}
        unit="(kW)"
        size="l"
      />

      <Link to="/issues">
        <div className="flex items-center justify-between p-4 rounded-xl text-white bg-[#EC4407] shadow-md shadow-neutral-800 hover:shadow-none hover:scale-105 transition-transform cursor-pointer">
          <div className="flex items-center gap-4">
            <WarningIcon className="w-8 h-8 fill-white" />
            <div className="flex flex-col">
              <span className="">Gestione</span>
              <span className="">Warning / Errori</span>
            </div>
          </div>
          <div className="flex gap-2 items-center text-5xl justify-self-end">
            <span className="font-bold">{warnings.length}</span>
            <span className="font-thin">/</span>
            <span className="font-bold">{errors.length}</span>
          </div>
        </div>
      </Link>

      <div className="w-full flex items-center gap-2 mt-2">
        <ButtonLink
          className="w-1/3 bg-blue-500"
          label="Gestione Ordini"
          icon={<OrdersIcon className="w-8 h-8 fill-white" />}
          to="/orders"
        />
        <ButtonLink
          className="w-1/3 bg-orange-500"
          icon={<IronIcon className="w-8 h-8 fill-white" />}
          label="Controllo Saldatura"
          to="/"
        />
        <ButtonLink
          className="w-1/3 bg-neutral-700"
          icon={<HistoryIcon className="w-8 h-8 fill-white" />}
          label="Storico Dati"
          to="/historic-data"
        />
      </div>
      <ExpandedSidebar open={expanded} parameters={line.current.parameters} />
    </div>
  );
};

const ButtonLink = (ps: {
  label: string;
  icon: React.ReactNode;
  to: string;
  className?: string;
}) => (
  <Link
    className={clsx(
      'text-white p-3 rounded-xl text-left text-sm flex flex-col font-semibold leading-tight shadow-md shadow-neutral-800',
      'hover:shadow-none hover:scale-105 transition-transform',
      ps.className,
    )}
    href={ps.to || '/'}>
    <span className="mb-4">{ps.icon}</span>
    {ps.label.split(' ').map((word, i) => (
      <span key={i}>{word}</span>
    ))}
  </Link>
);
